<template>
  <div class="legal-page">
    <div class="container py-4 py-lg-5">
      <div
        class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4"
      >
        <div>
          <router-link to="/" class="legal-back">
            <i class="ti ti-arrow-left me-2"></i>Back to site
          </router-link>
          <div class="mt-3">
            <hr-payroll-mark variant="full" />
          </div>
        </div>
        <div class="text-lg-end">
          <span class="badge bg-soft-primary text-primary px-3 py-2 rounded-pill">
            Privacy Policy
          </span>
          <div class="text-muted small mt-2">
            Last updated: 20 May 2026
          </div>
        </div>
      </div>

      <div class="card legal-card border-0 shadow-sm">
        <div class="card-body p-4 p-lg-5">
          <div class="mb-4">
            <h1 class="fw-bold mb-3">DoneQuick HR & Payroll Privacy Policy</h1>
            <p class="text-muted mb-0">
              This Privacy Policy explains how DoneQuick HR & Payroll collects, uses,
              stores, discloses, and protects personal data and company data processed
              through the platform. It applies to visitors, trial users, customers,
              administrators, employees, contractors, and other authorized users whose
              information is processed through the service.
            </p>
          </div>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">1. Information we may collect</h5>
            <p class="text-muted mb-2">Depending on how the service is used, we may process:</p>
            <ul class="legal-list text-muted mb-0">
              <li>Account and contact details such as names, email addresses, phone numbers, and company details.</li>
              <li>Employee and workforce data such as employee identifiers, job information, leave records, attendance, timesheets, payroll values, savings or loan records, and uploaded HR documents.</li>
              <li>Operational and system data such as audit logs, login events, IP addresses, browser information, and device metadata.</li>
              <li>Support and communication data such as requests sent through contact, demo, billing, or support forms.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">2. How we use personal data</h5>
            <p class="text-muted mb-2">We use data only where needed to operate and support the service, including to:</p>
            <ul class="legal-list text-muted mb-0">
              <li>Provide the platform and its configured modules.</li>
              <li>Create and manage company workspaces, user access, and subscriptions.</li>
              <li>Process payroll, attendance, approvals, reports, and related workflows configured by the customer.</li>
              <li>Secure the service, monitor misuse, and maintain auditability.</li>
              <li>Respond to support, demo, onboarding, sales, and billing requests.</li>
              <li>Comply with contractual, legal, tax, regulatory, and security obligations where applicable.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">3. Legal and operational basis</h5>
            <p class="text-muted mb-0">
              Where applicable, data is processed because it is necessary to perform a
              contract with the customer, to operate the requested service, to meet legal
              obligations, to protect legitimate business and security interests, or based
              on consent where consent is specifically requested.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">4. Customer control of workforce data</h5>
            <p class="text-muted mb-0">
              For most employee, payroll, attendance, leave, loan, savings, and internal
              business records stored in the platform, the customer company acts as the
              primary controller of that information. DoneQuick processes that data as
              needed to provide the service, subject to the customer’s configuration,
              instructions, and applicable law.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">5. Sharing and disclosure</h5>
            <p class="text-muted mb-2">We do not sell customer data. Information may be shared only where necessary, such as:</p>
            <ul class="legal-list text-muted mb-0">
              <li>With service providers that help us operate infrastructure, email delivery, payment processing, or support functions.</li>
              <li>With the customer’s own authorized users within the company workspace.</li>
              <li>Where required by law, lawful request, court order, or regulatory obligation.</li>
              <li>Where necessary to investigate misuse, protect rights, or secure the platform.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">6. Data retention</h5>
            <p class="text-muted mb-0">
              We retain data for as long as needed to provide the service, maintain
              security and audit history, comply with legal obligations, support account
              recovery, resolve disputes, and enforce commercial or platform terms. The
              exact retention period may vary depending on the data type, customer status,
              and applicable law.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">7. Security measures</h5>
            <p class="text-muted mb-2">
              We use reasonable technical and organizational measures to protect data,
              including access control, authentication controls, company-scoped isolation,
              audit trails, and operational safeguards.
            </p>
            <p class="text-muted mb-0">
              No system can guarantee absolute security. Customers should also use strong
              passwords, role-based permissions, careful user provisioning, and internal
              approval controls.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">8. International access and transfers</h5>
            <p class="text-muted mb-0">
              Where the platform, support, infrastructure, or service providers operate
              across regions, data may be processed in more than one jurisdiction. Where
              required, appropriate safeguards should be used for such processing.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">9. Your choices and rights</h5>
            <p class="text-muted mb-2">
              Subject to applicable law and the customer’s role as controller, a data
              subject may have rights such as:
            </p>
            <ul class="legal-list text-muted mb-0">
              <li>Requesting access to personal data.</li>
              <li>Requesting correction of inaccurate or incomplete data.</li>
              <li>Requesting deletion where retention is no longer required or lawful.</li>
              <li>Requesting restriction or objection to certain processing where applicable.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">10. Cookies and similar technologies</h5>
            <p class="text-muted mb-0">
              The platform and public pages may use cookies or similar browser storage for
              session management, security, login state, product preferences, and service
              performance. Disabling some browser storage functions may reduce platform
              usability.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">11. Changes to this policy</h5>
            <p class="text-muted mb-0">
              We may update this Privacy Policy from time to time. The latest version will
              be posted on this page and will apply from its effective date unless stated
              otherwise.
            </p>
          </section>

          <section>
            <h5 class="fw-bold mb-2">12. Questions or privacy requests</h5>
            <p class="text-muted mb-0">
              If you have privacy-related questions about the service, contact your company
              administrator or the official DoneQuick support or account channel connected
              to your workspace or commercial agreement.
            </p>
          </section>
        </div>
      </div>

      <div class="d-flex flex-wrap gap-3 justify-content-between align-items-center mt-4">
        <p class="mb-0 text-muted small">
          2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll
        </p>
        <div class="d-flex gap-2 flex-wrap">
          <router-link to="/terms-condition" class="btn btn-outline-secondary btn-sm">
            Terms & Conditions
          </router-link>
          <router-link to="/register" class="btn btn-primary btn-sm">
            Back to signup
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";

export default {
  components: {
    HrPayrollMark,
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background:
    radial-gradient(900px 420px at 100% 0%, rgba(37, 99, 235, 0.12), transparent 60%),
    linear-gradient(180deg, #fffdfb 0%, #f8fafc 100%);
}

.legal-card {
  border-radius: 24px;
}

.legal-back {
  color: #0f172a;
  font-weight: 600;
  text-decoration: none;
}

.legal-back:hover {
  color: #ff671f;
}

.bg-soft-primary {
  background: rgba(255, 103, 31, 0.12);
}

.legal-list {
  padding-left: 1.2rem;
}

.legal-list li + li {
  margin-top: 0.5rem;
}
</style>
